import axios, {
	type AxiosError,
	type AxiosInstance,
	type AxiosResponse,
	type InternalAxiosRequestConfig,
} from "axios";
import type { CreateApiClientConfig } from "./types.js";

function defaultErrorMessage(status: number): string {
	switch (status) {
		case 400:
			return "Invalid request";
		case 403:
			return "You don't have permission to do this";
		case 404:
			return "Resource not found";
		case 409:
			return "Conflict with existing data";
		case 429:
			return "Too many requests. Please try again later.";
		default:
			return "Something went wrong";
	}
}

/**
 * Creates an axios client with:
 *   - request interceptor:  x-timezone, FormData passthrough
 *   - response interceptor: 401 refresh-token queue, 429 retry-after,
 *                           default error toasting (via callbacks)
 *
 * Access token lives in an HttpOnly cookie set by the backend — the browser
 * sends it automatically on every request (withCredentials: true).
 * Only the refresh token is stored in localStorage for token rotation.
 */
export function createApiClient(config: CreateApiClientConfig): AxiosInstance {
	const {
		baseUrl: rawBaseUrl,
		tokenManager,
		refreshEndpoint = "/api/v1/auth/refresh-token",
		onUnauthorized,
		onError,
		onRateLimited,
		onNetworkError,
		onResponse,
	} = config;

	const baseUrl = rawBaseUrl.replace(/\/+$/, "");

	const client = axios.create({
		baseURL: baseUrl,
		withCredentials: true, // Send HttpOnly cookies with every request
		headers: {
			"Content-Type": "application/json",
			"Cache-Control": "no-cache, no-store, must-revalidate",
			Pragma: "no-cache",
			Expires: "0",
		},
	});

	let isRefreshing = false;
	// Queue holds promises that resolve once the refresh completes.
	// The resolve value is void — access token is in the HttpOnly cookie.
	let queue: Array<{
		resolve: () => void;
		reject: (reason: unknown) => void;
	}> = [];

	function flushQueue(error: unknown): void {
		for (const item of queue) {
			if (error) item.reject(error);
			else item.resolve();
		}
		queue = [];
	}

	client.interceptors.request.use(
		(req: InternalAxiosRequestConfig) => {
			// FormData: let the browser set the multipart boundary.
			if (
				typeof FormData !== "undefined" &&
				req.data instanceof FormData &&
				req.headers
			) {
				const headers = req.headers as {
					set?: (name: string, value: string | undefined) => void;
					delete?: (name: string) => void;
					[key: string]: unknown;
				};
				if (typeof headers.set === "function") {
					headers.set("Content-Type", undefined);
				} else if (typeof headers.delete === "function") {
					headers.delete("Content-Type");
				} else {
					delete headers["Content-Type"];
					delete headers["content-type"];
				}
			}

			// Access token is in HttpOnly cookie — no Authorization header needed.
			// The browser sends the cookie automatically via withCredentials.

			if (req.headers) {
				// CSRF defense: a custom header a cross-site form can't set. The
				// backend requires this on cookie-auth state-changing endpoints.
				req.headers["X-Requested-With"] = "XMLHttpRequest";

				// Auto-detect timezone — backend uses this for date-localized output.
				const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
				if (tz) req.headers["x-timezone"] = tz;
			}

			return req;
		},
		(error: AxiosError) => Promise.reject(error),
	);

	client.interceptors.response.use(
		(res: AxiosResponse) => {
			onResponse?.(res);
			return res;
		},
		async (error: AxiosError<{ message?: string }>) => {
			const original = error.config as InternalAxiosRequestConfig & {
				_retry?: boolean;
			};

			if (!error.response) {
				onNetworkError?.(error.config);
				return Promise.reject(error);
			}

			onResponse?.(error.response);

			const status = error.response.status;

			if (status === 429) {
				const headerValue = Number(error.response.headers?.["retry-after"]);
				const bodyValue = (
					error.response.data as { data?: { retryAfterSeconds?: number } }
				)?.data?.retryAfterSeconds;
				const retryAfter = !Number.isNaN(headerValue)
					? headerValue
					: (bodyValue ?? 60);
				onRateLimited?.(retryAfter, error.config);
				return Promise.reject(error);
			}

			if (status !== 401) {
				const message =
					error.response.data?.message || defaultErrorMessage(status);
				onError?.(status, message, error.config);
				return Promise.reject(error);
			}

			// 401: try refresh once.
			if (original._retry) return Promise.reject(error);

			// getRefreshToken() now reports whether a session marker exists (the
			// real refresh credential is the HttpOnly session cookie, which JS
			// cannot read). No marker => the caller is unauthenticated; don't
			// attempt a refresh.
			if (!tokenManager.getRefreshToken()) {
				return Promise.reject(error);
			}

			if (isRefreshing) {
				return new Promise<void>((resolve, reject) => {
					queue.push({ resolve, reject });
				}).then(() => {
					// Access token is in HttpOnly cookie — browser sends it on retry.
					original._retry = true;
					return client(original);
				});
			}

			isRefreshing = true;
			original._retry = true;
			try {
				// The calling app's session cookie (HttpOnly, scoped to the refresh
				// endpoint's path prefix) is sent automatically; the backend rotates
				// the session and sets a fresh access-token cookie. Nothing to read
				// or persist client-side.
				await axios.post(`${baseUrl}${refreshEndpoint}`, undefined, {
					withCredentials: true,
					// This bare call bypasses the request interceptor, so set the
					// CSRF header explicitly (refresh-token requires it).
					headers: { "X-Requested-With": "XMLHttpRequest" },
				});
				flushQueue(null);

				return client(original);
			} catch (refreshError) {
				flushQueue(refreshError);
				tokenManager.clearTokens();
				onUnauthorized?.();
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		},
	);

	return client;
}
